import { BrandfolderHexColors, FontIcon, FontIcons, StandardLink } from '@brandfolder/react';
import classNames from 'classnames';
import Link from 'next/link';
import { FunctionComponent, HTMLAttributes, useContext } from 'react';
import styled from 'styled-components';

import { BrandguideContext } from '@context/brandguide';
import { PermissionLevelContext } from '@context/permission-level';
import { WhitelabelContext } from '@context/whitelabel';

interface HeaderBreadcrumbsProps extends HTMLAttributes<HTMLDivElement> {
  home?: boolean;
}

const UnstyledHeaderBreadcrumbs: FunctionComponent<HeaderBreadcrumbsProps> = (props) => {
  const { home = false, ...otherProps } = props;

  const { brandguide } = useContext(BrandguideContext);
  const { editable } = useContext(PermissionLevelContext);
  const { whitelabel } = useContext(WhitelabelContext);

  const renderCaret = () => {
    return <FontIcon icon={FontIcons.CaretRight} />;
  };

  const renderMyBrandfoldersLink = () => {
    const href = `${whitelabel.BOULDER_URL}/organizations`;
    return (
      <div className="header__breadcrumb">
        <Link href={href} legacyBehavior>
          <StandardLink href={href} title={`Back to ${whitelabel.platformName}`}>
            My {whitelabel.libraryName.plural}
          </StandardLink>
        </Link>
      </div>
    );
  };

  const renderHomeLink = () => {
    return (
      <div className="header__breadcrumb">
        <Link href={whitelabel.BRANDGUIDE_URL} legacyBehavior>
          <StandardLink className="bf-link__standard--active" href={whitelabel.BRANDGUIDE_URL}>
            Brandguides
          </StandardLink>
        </Link>
      </div>
    );
  };

  const renderBrandguideLink = () => {
    if (!brandguide) return null;

    const { cname, name, slug } = brandguide;
    const href = cname ? `https://${cname}` : `/${slug}`;

    return (
      <div className="header__breadcrumb">
        <Link href={href} legacyBehavior>
          <StandardLink className="bf-link__standard--active" href={href} title={name}>
            {name}
          </StandardLink>
        </Link>
      </div>
    );
  };

  const isNotCname = !!brandguide?.cname === false;
  const isCnameAndEditable = !!brandguide?.cname && editable;
  const showBrandguideLink = isNotCname || isCnameAndEditable;

  return (
    <div
      {...otherProps}
      className={classNames({
        header__breadcrumbs: true,
        'header__breadcrumbs--home': home,
        [`${otherProps?.className}`]: !!otherProps?.className
      })}
    >
      {home ? (
        <>
          {renderMyBrandfoldersLink()}
          {renderCaret()}
          {renderHomeLink()}
        </>
      ) : brandguide ? (
        <>
          {renderMyBrandfoldersLink()}
          {showBrandguideLink && renderCaret()}
          {showBrandguideLink && renderBrandguideLink()}
        </>
      ) : null}
    </div>
  );
};

const StyledHeaderBreadcrumbs = styled(UnstyledHeaderBreadcrumbs)`
  align-items: center;
  display: flex;
  margin: 0 130px 2px 24px;

  .header__breadcrumb {
    flex-shrink: 0;
    height: 24px;

    &:last-child {
      max-width: 100%;

      .bf-link__standard {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
      }
    }
  }

  .bf-icon__font {
    flex-shrink: 0;
    font-size: 8px;
    margin: 4px 8px 0;
  }

  .bf-link__standard {
    color: ${BrandfolderHexColors.Text};
    font-family: Circular-Book, sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 26px;
    padding: 0 4px;
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: ${BrandfolderHexColors.Text};
      text-decoration: underline;
    }

    &:visited {
      color: ${BrandfolderHexColors.Text};
    }

    &:active {
      color: ${BrandfolderHexColors.GrayDark};
    }

    &:focus-within {
      outline-color: ${BrandfolderHexColors.Text};
    }

    &.bf-link__standard--active {
      font-family: Circular-Medium, sans-serif;
    }
  }
`;

export const HeaderBreadcrumbs: FunctionComponent<HeaderBreadcrumbsProps> = (props) => {
  return <StyledHeaderBreadcrumbs {...props} />;
};
